import { useEffect, useMemo, useRef, useState } from "react";
import { api, brl, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ChevronDown, ClipboardList, Smile, Frown, Plus, Check, Loader2, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { printPdfBlob, preopenForIOS } from "@/lib/print";

export const TC_STATES = [
  { k: "bom", l: "Bom", icon: Smile, on: "bg-green-600 border-green-500 text-white", txt: "text-green-400" },
  { k: "ruim", l: "Ruim (trocar)", icon: Frown, on: "bg-red-600 border-red-500 text-white", txt: "text-red-400" },
];
const key = (s, i) => `${s}|${i}`;
const norm = (e) => (e?.state === "atencao" ? { ...e, state: "ruim" } : e);

function ItemRow({ section, item, e, set, onAdd, adding }) {
  const st = e?.state;
  return (
    <div data-testid={`tc-item-${key(section, item)}`} className={`py-2 border-b border-zinc-800/70 ${st ? "" : ""}`}>
      <div className="flex items-center gap-2">
        <span className="flex-1 min-w-0 text-sm text-zinc-200">{item}</span>
        {TC_STATES.map(({ k, l, icon: Icon, on }) => (
          <button key={k} type="button" title={l} aria-label={l} data-testid={`tc-${k}-${key(section, item)}`}
            onClick={() => set({ state: st === k ? null : k })}
            className={`h-10 w-10 sm:h-9 sm:w-9 shrink-0 rounded-full border flex items-center justify-center transition-colors ${st === k ? on : "border-zinc-700 text-zinc-500 hover:text-zinc-200"}`}>
            <Icon className="h-5 w-5" />
          </button>
        ))}
      </div>
      {st && (
        <div className="mt-2 grid grid-cols-[70px_1fr_100px] gap-2 items-center">
          <Input type="number" inputMode="decimal" placeholder="Qtd" value={e.qty ?? ""} onChange={(ev) => set({ qty: ev.target.value === "" ? null : Number(ev.target.value) })} className="h-9 bg-zinc-900 border-zinc-800" data-testid={`tc-qty-${key(section, item)}`} />
          <Input placeholder="Observações" value={e.note || ""} onChange={(ev) => set({ note: ev.target.value })} className="h-9 bg-zinc-900 border-zinc-800" data-testid={`tc-note-${key(section, item)}`} />
          <Input type="number" inputMode="decimal" step="0.01" placeholder="Valor R$" value={e.value ?? ""} onChange={(ev) => set({ value: ev.target.value === "" ? null : Number(ev.target.value) })} className="h-9 bg-zinc-900 border-zinc-800" data-testid={`tc-value-${key(section, item)}`} />
          {st === "ruim" && (
            <button type="button" disabled={e.in_budget || adding} onClick={onAdd} data-testid={`tc-add-${key(section, item)}`}
              className={`col-span-3 justify-self-start inline-flex items-center gap-1 h-8 px-3 rounded-md text-xs font-bold ${e.in_budget ? "bg-zinc-800 text-green-300" : "bg-sky-600 hover:bg-sky-500 text-white"}`}>
              {e.in_budget ? <><Check className="h-3.5 w-3.5" /> No orçamento</> : adding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Plus className="h-3.5 w-3.5" /> Adicionar ao orçamento</>}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export function TechChecklistCard({ order, onUpdated }) {
  const [tpl, setTpl] = useState(null);
  const [map, setMap] = useState(() => Object.fromEntries(((order.tech_checklist || {}).entries || []).map((e) => [key(e.section, e.item), norm(e)])));
  const [open, setOpen] = useState({});
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState(null);
  const dirty = useRef(false);
  useEffect(() => { api.get("/tech-checklist/template").then(({ data }) => setTpl(data.sections)).catch(() => setTpl([])); }, []);
  useEffect(() => {
    if (!dirty.current) { return undefined; }
    const t = setTimeout(async () => {
      setSaving(true);
      try { await api.put(`/orders/${order.id}/tech-checklist`, { entries: Object.values(map) }); dirty.current = false; }
      catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
    }, 900);
    return () => clearTimeout(t);
  }, [map, order.id]);
  const set = (s, i, patch) => { dirty.current = true; setMap((m) => ({ ...m, [key(s, i)]: { section: s, item: i, ...(m[key(s, i)] || {}), ...patch } })); };
  const counts = useMemo(() => Object.values(map).reduce((a, e) => { if (e.state) { a[e.state] = (a[e.state] || 0) + 1; } return a; }, {}), [map]);
  const addToBudget = async (s, i) => {
    const e = map[key(s, i)];
    const b = order.budget;
    if (b?.decision === "aprovado" && !window.confirm("O orçamento já foi aprovado. Adicionar este item cria um novo orçamento para o cliente aprovar de novo. Continuar?")) { return; }
    setAdding(key(s, i));
    try {
      const items = [...((b?.items) || []), { description: e.note ? `${i} — ${e.note}` : i, quantity: e.qty || 1, unit_price: e.value || 0 }];
      const { data } = await api.put(`/orders/${order.id}/budget`, { items, notes: b?.notes || null });
      onUpdated?.(data);
      set(s, i, { in_budget: true });
      toast.success("Item adicionado ao orçamento");
    } catch (er) { toast.error(formatApiError(er)); } finally { setAdding(null); }
  };
  const printPdf = async (filled) => {
    const win = preopenForIOS();
    try { const { data } = await api.get(`/orders/${order.id}/tech-checklist/pdf`, { params: { filled }, responseType: "blob" }); printPdfBlob(data, win); }
    catch (e) { if (win) win.close(); toast.error(formatApiError(e)); }
  };
  if (!tpl) { return null; }
  return (
    <section data-testid="tech-checklist-card" className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="font-bold text-zinc-100 flex items-center gap-2"><ClipboardList className="h-4 w-4 text-green-brand" /> Checklist completo (inspeção técnica)</h3>
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" variant="outline" data-testid="tc-print-filled" onClick={() => printPdf(true)} className="bg-zinc-900 border-zinc-800"><Printer className="h-4 w-4 mr-1" /> Imprimir preenchido</Button>
          <Button size="sm" variant="outline" data-testid="tc-print-blank" onClick={() => printPdf(false)} className="bg-zinc-900 border-zinc-800"><Printer className="h-4 w-4 mr-1" /> Em branco</Button>
        </div>
      </div>
      <div className="flex items-center gap-3 text-xs flex-wrap">
        {TC_STATES.map(({ k, l, icon: Icon, txt }) => <span key={k} className={`inline-flex items-center gap-1 ${txt}`}><Icon className="h-4 w-4" /> {counts[k] || 0} <span className="hidden sm:inline">{l}</span></span>)}
        {saving && <Loader2 className="h-3.5 w-3.5 animate-spin text-zinc-500" />}
      </div>
      <p className="text-[11px] text-zinc-500">Toque na carinha para marcar. Itens em Ruim podem ir direto para o orçamento. Salva sozinho. A lista é editável em Configurações → Atendimento.</p>
      {tpl.map((sec) => {
        const marked = sec.items.filter((i) => map[key(sec.title, i)]?.state).length;
        const isOpen = !!open[sec.title];
        return (
          <div key={sec.title} className="rounded-xl border border-zinc-800 bg-zinc-950/50">
            <button type="button" data-testid={`tc-section-${sec.title}`} onClick={() => setOpen((p) => ({ ...p, [sec.title]: !p[sec.title] }))}
              className="w-full flex items-center justify-between px-3 py-3 text-left">
              <span className="text-sm font-bold text-zinc-100">{sec.title}</span>
              <span className="flex items-center gap-2 text-xs text-zinc-400"><span className={marked ? "text-green-300 font-bold" : ""}>{marked}/{sec.items.length}</span><ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`} /></span>
            </button>
            {isOpen && <div className="px-3 pb-2">{sec.items.map((i) => (
              <ItemRow key={i} section={sec.title} item={i} e={map[key(sec.title, i)]} set={(p) => set(sec.title, i, p)} onAdd={() => addToBudget(sec.title, i)} adding={adding === key(sec.title, i)} />
            ))}</div>}
          </div>
        );
      })}
    </section>
  );
}

export function TechChecklistView({ entries, testid = "tech-checklist-view" }) {
  const list = (entries || []).filter((e) => e.state).map(norm);
  if (!list.length) { return null; }
  const rank = { ruim: 0, bom: 1 };
  return (
    <ul data-testid={testid} className="space-y-1.5">
      {[...list].sort((a, b) => rank[a.state] - rank[b.state]).map((e, idx) => {
        const s = TC_STATES.find((x) => x.k === e.state);
        const Icon = s.icon;
        return (
          <li key={idx} className="flex items-start gap-2 text-sm">
            <Icon className={`h-5 w-5 shrink-0 ${s.txt}`} />
            <div className="min-w-0"><span className="text-zinc-100">{e.item}</span> <span className={`text-xs font-bold ${s.txt}`}>{s.l}</span>{e.qty ? <span className="text-xs text-zinc-400"> · {e.qty}x</span> : null}
              {e.value ? <span className="text-xs text-zinc-400"> · {brl(e.value)}</span> : null}
              <div className="text-[11px] text-zinc-500">{e.section}{e.note ? ` — ${e.note}` : ""}</div></div>
          </li>
        );
      })}
    </ul>
  );
}
